import { DeviceStatus, SensorType } from "constant/gateway";
import { ChartAttribute } from "generated/models";
import { chunk, cloneDeep, get, isEmpty, omit, orderBy, sumBy } from "lodash";
import moment, { Moment, unitOfTime } from "moment";

import { defaultChartRefreshRate } from "constant";
import LocalStorageService from "service/localStorageService";
import { getSensorLastReading } from "service/gatewayService";
import WebsocketQuery from "service/websocketQuery";
import {
    BarChartSeries,
    ConsumptionChartBy,
    ConsumptionChartData,
    PSLStatus,
} from "types";
import { DeviceInfo } from "types/Charts";
import { SensorDetails } from "types/Sensors";
import {
    addDecimalPlace,
    combineResultArrays,
    convertPrometheusTimeToSeconds,
    convertSecondsToPrometheusTime,
    formattedChartValue,
    getYaxisDetails,
    isBinarySensor,
    isHttpSuccess,
    monthsToSeconds,
    toBinaryValue,
    toFiniteReadingNumber,
    totalDoesNotExceedMax,
} from "utils/functions";
import { getDeviceStatus } from "utils/gatewayFunctions";
import { getSensorType } from "utils/sensorFunctions";

type MetricType = "waterflow" | "energy";

type MetricValue = [number, string];

type QueryTime = string | number | null;

export type MetricData = Pick<ChartAttribute, "device_id" | "said" | "gateway_id"> & {
    values: MetricValue[];
};

type QueryResult = {
    metric: {
        uid: string;
        said: string;
        gateway: string;
    };
    values: MetricValue[];
};

type MetricValueFilter = {
    /** The data's timestamp must be greater than or equal to this value */
    onOrAfter: Moment;

    /** The data's timestamp must be less than this value */
    before?: Moment;

    /** The data's timestamp must be less than or equal to this value */
    onOrBefore?: Moment;
};

const isMetricValueTimestampInFilter = (
    timestamp: number,
    filter: MetricValueFilter
): boolean => {
    if (timestamp < filter.onOrAfter.unix()) {
        return false;
    }

    if (filter.before && timestamp >= filter.before.unix()) {
        return false;
    }

    if (filter.onOrBefore && timestamp > filter.onOrBefore.unix()) {
        return false;
    }

    return true;
};

type MetricDataForComparison = {
    metricData: MetricData[];
    previousMetricData: MetricData[];
};

/**
 * Get browser's timezone offset in hours
 * Uses Date.getTimezoneOffset() which is universally supported
 *
 * Examples:
 * - Vietnam (UTC+7): getTimezoneOffset() returns 7
 * - US Eastern (UTC-5): getTimezoneOffset() returns -5
 * - UK (UTC+0): getTimezoneOffset() returns 0
 * - Japan (UTC+9): getTimezoneOffset() returns 9
 * - India (UTC+5:30): getTimezoneOffset() returns 5.5
 * - Nepal (UTC+5:45): getTimezoneOffset() returns 5.75
 */
export const getTimezoneOffset = (): number => {
    try {
        const offsetMinutes = new Date().getTimezoneOffset();
        const offsetHours = -offsetMinutes / 60; // Convert to hours and invert sign
        return offsetHours;
    } catch (error) {
        console.warn(
            "Failed to detect timezone, falling back to UTC+7:",
            error
        );
        return 7; // Fallback to UTC+7
    }
};

const WS = new WebsocketQuery();

/**
 * Same query for all ranges - using consumption metrics for WebSocket queries
 */
export const CombinedConsumptionQuery: Record<MetricType, string> = {
    waterflow: "mqtt2tsdb_flowvolume_m3",
    energy: "mqtt2tsdb_activeenergy_kwh",
};

// Pre-defined query metrics for combined chart
type CombinedChartRawQuery = "mqtt2tsdb_gauge_na";
enum CombinedChartResolutionQuery {
    "1m" = "avg_1m_gauge_na",
    "5m" = "avg_5m_gauge_na",
    "30m" = "avg_30m_gauge_na",
    "1h" = "avg_1h_gauge_na",
}
type AggregationMetric = keyof typeof CombinedChartResolutionQuery;
type CombinedChartQuery = CombinedChartRawQuery | CombinedChartResolutionQuery;
const DefaultCombinedChartRawQuery: CombinedChartRawQuery =
    "mqtt2tsdb_gauge_na";

export const timeUnitMapping: Record<
    ConsumptionChartBy,
    moment.unitOfTime.Base
> = {
    [ConsumptionChartBy.Day]: "day",
    [ConsumptionChartBy.Week]: "week",
    [ConsumptionChartBy.Month]: "month",
    [ConsumptionChartBy.Year]: "year",
};

/**
 * Convert moment unitOfTime.Base back to ConsumptionChartBy
 */
const getConsumptionChartByFromTimeUnit = (
    timeUnit: moment.unitOfTime.Base
): ConsumptionChartBy => {
    switch (timeUnit) {
        case "day":
            return ConsumptionChartBy.Day;
        case "week":
            return ConsumptionChartBy.Week;
        case "month":
            return ConsumptionChartBy.Month;
        case "year":
            return ConsumptionChartBy.Year;
        default:
            return ConsumptionChartBy.Day; // fallback
    }
};

/**
 * Check if chart period requires timezone offset for proper data alignment
 */
const requiresTimezoneOffset = (groupBy: ConsumptionChartBy): boolean => {
    return (
        groupBy === ConsumptionChartBy.Week ||
        groupBy === ConsumptionChartBy.Month ||
        groupBy === ConsumptionChartBy.Year
    );
};

const getCurrentAndPreviousTime = (
    queryTime: number,
    timeUnit: unitOfTime.Base
) => {
    const time = moment.unix(queryTime);
    const previousTime = moment(time).subtract(1, timeUnit);
    return {
        time,
        previousTime,
    };
};

const CHART_TYPE_SINGLE = "SINGLE";

const consumptionQuery = (query: string) =>
    query
        .replace(/mqtt2tsdb_activepower_kw/, "mqtt2tsdb_activeenergy_kwh")
        .replace(/mqtt2tsdb_flowrate_m3ph/, "mqtt2tsdb_flowvolume_m3");

const changeDataTo2DP = (data: MetricData, chart_type: string): MetricData => {
    if (!data?.values || !Array.isArray(data.values)) {
        return data;
    }

    // Create a deep copy to avoid mutating the input
    const transformedData: MetricData = {
        ...data,
        values: data.values.map((valueEntry: MetricValue): MetricValue => {
            const [timestamp, value] = valueEntry;
            const newTimestamp =
                chart_type === CHART_TYPE_SINGLE ? timestamp * 1000 : timestamp;
            const numericValue = Number.parseFloat(value);
            const formattedValue = Number.isNaN(numericValue)
                ? "0.00"
                : numericValue.toFixed(2);

            return [newTimestamp, formattedValue];
        }),
    };

    return transformedData;
};

const _chartMissingOption = {
    type: "line",
    itemStyle: {
        color: "background: rgba(243, 199, 173, 0.05)",
    },
    lineStyle: {
        color: "rgba(251, 108, 89, 1)",
        type: "dashed",
    },
    smooth: true,
    showSymbol: false,
    areaStyle: {
        origin: "start",
    },
    markLine: {
        symbol: ["none", "none"],
        lineStyle: {
            color: "#ECA48E",
            type: "dashed",
        },
        label: {
            show: false,
        },
        data: [
            {
                xAxis: 1652672793000,
            },
            {
                xAxis: 1652672862000,
            },
        ],
    },
};

const splitSeriesMissing = ({
    result,
    gapTime,
    chartOptions,
    isBinaryChart,
    allowanceMultiplier = 1.5,
}: {
    result: any[];
    gapTime: number;
    chartOptions: any;
    isBinaryChart: boolean;
    /** Multiplier applied to (gapTime in seconds) when comparing consecutive samples. Default 1.5 matches historical chart behavior. */
    allowanceMultiplier?: number;
}) => {
    const _result = cloneDeep(result);

    const combinedResult: any = {
        metric: {},
        values: [],
    };

    for (const r of _result) {
        combinedResult.metric = r.metric;
        const mapped = r.values.map((v: any) => [v[0] * 1000, v[1]]);
        combinedResult.values.push(...mapped);
    }

    const dataArray = orderBy(combinedResult.values, ["[0]"], ["asc"]);

    let seriesGroup = [];
    let seriesGroupItem = [];
    let tailIndex = 0;
    let headIndex = 1;
    const interval = gapTime / 1000;
    const allowanceMargin = allowanceMultiplier;

    for (tailIndex; tailIndex < dataArray.length; tailIndex++) {
        const difference =
            (dataArray[headIndex]?.[0] - dataArray[tailIndex]?.[0]) / 1000;
        let isMissing = false;
        isMissing =
            dataArray.length > 1 && difference > interval * allowanceMargin;

        if (isMissing) {
            if (seriesGroup.length === 0) {
                // first missing
                seriesGroupItem.push(dataArray[tailIndex]);
                seriesGroup.push({
                    label: "missing",
                    data: seriesGroupItem,
                    ...(isBinaryChart && { step: "end" }),
                    ..._chartMissingOption,
                });
            } else {
                // subsequent missing
                const currentLabel = seriesGroup.at(-1).label;
                if (currentLabel === "missing") {
                    // if latest label is missing, continue push
                    seriesGroupItem.push(dataArray[tailIndex]);
                } else {
                    // if latest label is not missing, create new missing group
                    seriesGroup.at(-1).data.push(dataArray[tailIndex]);
                    seriesGroupItem = [];
                    seriesGroupItem.push(dataArray[tailIndex]);
                    seriesGroup.push({
                        label: "missing",
                        data: seriesGroupItem,
                        ...(isBinaryChart && { step: "end" }),
                        ..._chartMissingOption,
                    });
                }
            }
        } else if (seriesGroup.length === 0) {
            // first non-missing
            seriesGroupItem.push(dataArray[tailIndex]);
            seriesGroup.push({
                data: seriesGroupItem,
                label: "not-missing",
                ...(isBinaryChart && { step: "end" }),
                ...chartOptions,
            });
        } else {
            // subsequent non-missing
            const currentLabel = seriesGroup.at(-1).label;
            if (currentLabel === "not-missing") {
                // if latest label is not missing, continue push
                seriesGroupItem.push(dataArray[tailIndex]);
            } else {
                // if latest label is missing, create new not-missing group
                seriesGroup.at(-1).data.push(dataArray[tailIndex]);
                seriesGroupItem = [];
                seriesGroupItem.push(dataArray[tailIndex]);
                seriesGroup.push({
                    data: seriesGroupItem,
                    label: "not-missing",
                    ...(isBinaryChart && { step: "end" }),
                    ...chartOptions,
                });
            }
        }

        headIndex++;
    }

    return {
        names: [combinedResult?.metric?.name],
        series: seriesGroup,
    };
};

/** Gaps longer than this (seconds) between consecutive samples are drawn as a separate "missing" segment. */
export const SPARKLINE_GAP_SECONDS = 60 * 60;

/** ECharts missing style for sparklines — no `markLine`, no area fill (full chart uses `areaStyle.origin` which fills under the line). */
const sparklineMissingLineBase = {
    type: "line" as const,
    silent: true,
    tooltip: {
        show: false,
    },
    itemStyle: {
        color: "rgba(251, 108, 89, 1)",
    },
    lineStyle: _chartMissingOption.lineStyle,
    smooth: _chartMissingOption.smooth,
    showSymbol: false,
    areaStyle: {
        color: "transparent",
        opacity: 0,
    },
};

export const finalizeSparklineSeriesFromSplit = (series: any[]) => {
    let missingIdx = 0;
    return series.map((s) => {
        /** `label` is internal to splitSeriesMissing; ECharts `series.label` must be an object — a string breaks axis tooltip (getRawIndex). */
        const cleaned = omit(s, ["markLine", "label"]);
        if (s.label === "missing") {
            return {
                ...cleaned,
                ...sparklineMissingLineBase,
                id: `missing-${missingIdx++}`,
            };
        }
        return cleaned;
    });
};

/**
 * Split padded sparkline points (timestamps in ms for ECharts) into dashed "missing" spans when the
 * gap between consecutive points is strictly greater than `gapSeconds`.
 */
const buildSparklineSegmentsFromPadded = (
    padded: [number, number | string][],
    gapSeconds: number,
    chartOptions: any,
    isBinaryChart: boolean
): any[] => {
    if (padded.length < 2) {
        return [];
    }
    const segments: any[] = [];
    let i = 0;
    while (i < padded.length - 1) {
        const dtSeconds =
            (padded[i + 1][0] - padded[i][0]) / 1000;
        if (dtSeconds > gapSeconds) {
            segments.push({
                label: "missing",
                data: [
                    [padded[i][0], padded[i][1]],
                    [padded[i + 1][0], padded[i + 1][1]],
                ],
                ...(isBinaryChart && { step: "end" }),
                ..._chartMissingOption,
            });
            i += 1;
        } else {
            const startIdx = i;
            let j = i;
            while (
                j < padded.length - 1 &&
                (padded[j + 1][0] - padded[j][0]) / 1000 <= gapSeconds
            ) {
                j += 1;
            }
            segments.push({
                label: "not-missing",
                data: padded.slice(startIdx, j + 1).map(([t, v]) => [t, v]),
                ...(isBinaryChart && { step: "end" }),
                ...chartOptions,
            });
            i = j;
        }
    }
    return segments;
};

/**
 * Build ECharts line series for a sparkline: clip to the 24h window ending at `nowMs`,
 * extend the polyline to the window edges using the first/last sample values when samples do not reach the edges,
 * then split gaps longer than `gapSeconds` into dashed missing segments (no tooltip) vs solid data segments.
 * When there are no samples in the window, returns a single full-window missing segment.
 */
export const buildSparklineSeriesWithMissingGaps = ({
    points,
    color,
    isBinaryChart,
    accuracy,
    nowMs = Date.now(),
    gapSeconds = SPARKLINE_GAP_SECONDS,
}: {
    points: [number, number][];
    color: string;
    isBinaryChart: boolean;
    accuracy: number;
    nowMs?: number;
    gapSeconds?: number;
}): any[] => {
    /** Same local clock time yesterday (e.g. query at 10:37 today → from 10:37 yesterday). */
    const rangeStartMs = moment(nowMs).subtract(1, "day").valueOf();
    const rangeEndMs = nowMs;

    const filtered = orderBy(
        points.filter(([t]) => t >= rangeStartMs && t <= rangeEndMs),
        ["[0]"],
        ["asc"]
    );

    if (filtered.length === 0) {
        return finalizeSparklineSeriesFromSplit([
            {
                label: "missing",
                type: "line",
                data: [
                    [rangeStartMs, 0],
                    [rangeEndMs, 0],
                ],
            },
        ]);
    }

    let normalizedPoints: [number, number][] = isBinaryChart
        ? filtered.map(([t, v]) => [t, toBinaryValue(Number(v))])
        : filtered;

    const valueFormatter = (value: unknown) =>
        formattedChartValue({
            sensorAccuracy: isBinaryChart ? 2 : accuracy,
            value,
        });

    let padded = normalizedPoints as [number, number | string][];

    const first = padded[0];
    const last = padded.at(-1)!;
    const firstT = first[0];
    const lastT = last[0];
    const firstV = first[1];
    const lastV = last[1];

    if (firstT > rangeStartMs) {
        padded = [[rangeStartMs, firstV], ...padded];
    }
    if (lastT < rangeEndMs) {
        padded = [...padded, [rangeEndMs, lastV]];
    }

    const chartOptions = {
        type: "line",
        smooth: !isBinaryChart,
        showSymbol: false,
        lineStyle: {
            color,
            width: 2,
        },
        itemStyle: {
            color,
        },
        areaStyle: {
            color: "transparent",
        },
        tooltip: {
            valueFormatter,
        },
    };

    const series = buildSparklineSegmentsFromPadded(
        padded,
        gapSeconds,
        chartOptions,
        isBinaryChart
    );

    return finalizeSparklineSeriesFromSplit(series);
};

const _getSampleQuery = ({
    response,
    gapTime,
    chartColor,
    prepareAggregated,
    minResponse,
    maxResponse,
    name,
}: any) => {
    const funcRet: {
        info: any;
        names: any;
        series: any;
        aggregatedSeries: any;
    } = {
        info: get(response, "data.data.result[0].metric", {}),
        names: [],
        series: [],
        aggregatedSeries: undefined,
    };

    const currentChartOption = {
        type: "line",
        smooth: true,
        showSymbol: false,
        itemStyle: {
            color: chartColor,
        },
    };

    const deviceInfo = funcRet.info;
    const { isBinary } = getYaxisDetails({
        appId: deviceInfo.app,
        object: deviceInfo.object,
        said: deviceInfo.said,
    });
    const { names: seriesGroupNames, series: seriesGroup } = splitSeriesMissing(
        {
            result: get(response, "data.data.result", []),
            gapTime,
            chartOptions: currentChartOption,
            isBinaryChart: isBinary,
        }
    );

    if (prepareAggregated) {
        if (
            !get(response, "data.data.result[0]", {}) ||
            !get(minResponse, "data.data.result[0]", {}) ||
            !get(maxResponse, "data.data.result[0]", {})
        ) {
            return {
                info: {},
                names: [""],
                series: [],
            };
        }

        // BE may return multiple data arrays. Combine them and remove duplicate timestamps
        combineResultArrays(response);
        combineResultArrays(minResponse);
        combineResultArrays(maxResponse);

        const results = changeDataTo2DP(
            response.data.data.result[0],
            CHART_TYPE_SINGLE
        );

        const minResults = changeDataTo2DP(
            minResponse.data.data.result[0],
            CHART_TYPE_SINGLE
        );
        const maxResults = changeDataTo2DP(
            maxResponse.data.data.result[0],
            CHART_TYPE_SINGLE
        );
        const series: any = [];
        const resBody = {
            symbolSize: 15,
            color: chartColor,
            type: "scatter",
            name,
            data: results.values,
        };

        const maxBody = {
            symbolSize: 7,
            color: "#9BD88C",
            type: "scatter",
            name: "Max",
            data: maxResults.values,
        };
        const minBody = {
            symbolSize: 7,
            color: "#ED8787",
            type: "scatter",
            name: "Min",
            data: minResults.values,
        };
        const lineDataList = (
            readinglst: any,
            maxReadinglst: any,
            minReadingLst: any
        ) => {
            const body = [];
            for (let i = 0; i < readinglst.values.length; i++) {
                body.push(
                    cloneDeep(readinglst.values[i]),
                    maxReadinglst.values[i],
                    minReadingLst.values[i]
                );
            }

            for (let i = 0; i < body.length; i = i + 3) {
                body[i][1] = Number.NaN;
            }

            return body;
        };
        const lineBody = {
            color: "#BECAE3",
            type: "line",
            zlevel: -1,
            data: lineDataList(results, maxResults, minResults),
        };
        series.push(resBody, maxBody, minBody, lineBody);
        funcRet.names = [get(results, "metric.name", ""), "Max", "Min"];
        funcRet.aggregatedSeries = series;
        funcRet.series = seriesGroup;
    }

    funcRet.names = seriesGroupNames;
    funcRet.series = seriesGroup;

    return funcRet;
};

const getAggregateBy = ({
    timeValue,
    numberOfChart,
}: {
    timeValue: number;
    numberOfChart: number;
}): AggregationMetric => {
    // Requirement: if > 6mths, use hourly aggregation
    if (timeValue > monthsToSeconds(6)) {
        return "1h";
    }

    const aggregationInArray: AggregationMetric[] = ["1m", "5m", "30m", "1h"];

    for (let aggregation of aggregationInArray) {
        const totalAggregatedSample = Math.floor(
            timeValue / convertPrometheusTimeToSeconds(aggregation)
        );

        if (
            totalDoesNotExceedMax({
                total: totalAggregatedSample,
                numberOfChart: numberOfChart,
            })
        ) {
            return aggregation;
        }
    }

    // Fallback to default aggregation if no suitable one is found, return 1h is more safe. This is not expected to be reached.
    return "1h";
};

const roundTimestamp = (date: Date | number, seconds: number) => {
    if (typeof date === "number") {
        date = new Date(date);
    }

    const roundedDate = new Date(
        Math.ceil(date.getTime() / (seconds * 1000)) * seconds * 1000
    );

    return Math.floor(roundedDate.getTime() / 1000);
};

export const fetchMetricData = async (params: any = {}) => {
    const orgId = LocalStorageService.getItem("orgId");

    if (!WS.isReady(orgId)) {
        await WS.initConnection(orgId);
    }

    let payload = params;
    if (typeof params === "string") {
        // Url encode the params if it's a string
        const url = new URL(params);
        payload = Object.fromEntries(url.searchParams.entries());
    }

    // Remove some unnecessary properties from payload to prevent sending them over WebSocket
    delete payload.token;
    delete payload.originQuery;

    const response = await WS.fetch(payload);

    return {
        data: {
            data: {
                result: response.data,
            },
        },
    };
};

const getMQTTquery: any = ({
    queryType,
    query,
    time_range,
    lastTimestamp,
    timeValue,
    aggregationType,
    numberOfChart,
    isCombinedChart = false,
    sensorReportRate,
}: {
    queryType: "raw" | "aggregated";
    query: string;
    time_range: string;
    lastTimestamp: string;
    timeValue: number;
    aggregationType?: "avg" | "min" | "max";
    numberOfChart: number;
    isCombinedChart?: boolean;
    sensorReportRate?: number;
}) => {
    if (isCombinedChart) {
        let queryMetric: CombinedChartQuery = DefaultCombinedChartRawQuery;
        let resolution: string = convertSecondsToPrometheusTime(
            sensorReportRate ?? defaultChartRefreshRate
        );

        if (queryType === "aggregated") {
            const aggregatedResolution = getAggregateBy({
                timeValue,
                numberOfChart,
            });
            resolution = aggregatedResolution;
            queryMetric = CombinedChartResolutionQuery[aggregatedResolution];

            // Special cases, required from the backend, resolutions same as the metrics will not work
            if (
                [
                    CombinedChartResolutionQuery["30m"],
                    CombinedChartResolutionQuery["1h"],
                ].includes(queryMetric)
            ) {
                resolution = "5m";
            }
        }

        const resolutionInSeconds = convertPrometheusTimeToSeconds(resolution);
        const roundedTimestamp = roundTimestamp(
            new Date(Number(lastTimestamp) * 1000),
            resolutionInSeconds
        );

        return {
            body: {
                time_series: queryMetric,
                time_range,
                time: roundedTimestamp,
                resolution,
                post_op: "SUM",
            },
        };
    }

    if (queryType === "raw") {
        return {
            body: {
                query: `${query}[${time_range}]`,
                time: lastTimestamp,
            },
        };
    } else if (queryType === "aggregated") {
        const customQuery = query.replace("mqtt2tsdb", "");
        const aggregationBy = getAggregateBy({
            timeValue,
            numberOfChart,
        });

        return {
            body: {
                query: `${aggregationType}_${aggregationBy}${customQuery}[${time_range}]`,
                time: lastTimestamp,
            },
        };
    }
};

export const groupDataByTime = (
    groupBy: ConsumptionChartBy,
    data: any[],
    lastTime: Moment
): {
    categories: string[] | number[];
    data: any[];
} => {
    switch (groupBy) {
        case ConsumptionChartBy.Week:
            return groupDataByWeek(data);
        case ConsumptionChartBy.Month:
            return groupDataByMonth(data, lastTime);
        case ConsumptionChartBy.Year:
            return groupDataByYear(data);
        default:
            return groupDataByHour(data);
    }
};

export const groupDataByHour = (data: any[]) => {
    const pad = (num: number) => String(num).padStart(2, "0");
    const hours = Array.from(new Array(24).keys()).map((i: number, index) =>
        pad(i)
    );
    const groupData = isEmpty(data)
        ? []
        : hours.reduce((acc, hh) => {
            const hourData = data.filter((t: any) => {
                const endTime = moment.unix(t?.[0]);
                const thh = endTime.hours();

                return thh === +hh;
            });

            const groupValue = isEmpty(hourData)
                ? undefined
                : sumBy(hourData, (t) => Number(t[1]));

            acc.push(groupValue);
            return acc;
        }, [] as any[]);

    return {
        categories: hours.map((h) => moment(h, "HH").format("hA")),
        data: groupData,
    };
};

export const groupDataByWeek = (data: any[]) => {
    const days = Array.from(new Array(7).keys()).map((i: number, index) => i);
    const groupData = isEmpty(data)
        ? []
        : days.reduce((acc, day) => {
            const dayData = data.filter((t: any) => {
                const endTime = moment.unix(t?.[0]);

                const tDay = endTime.day();
                return tDay === day;
            });

            // Check empty data to prevent the sumBy from returning 0
            const groupValue = isEmpty(dayData)
                ? undefined
                : sumBy(dayData, (t) => Number(t[1]));

            acc.push(groupValue);
            return acc;
        }, [] as any[]);

    return {
        categories: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        data: groupData,
    };
};
export const groupDataByMonth = (data: any[], lastTime: Moment) => {
    const daysInMonth = lastTime.daysInMonth();
    const days = Array.from(new Array(daysInMonth).keys()).map(
        (i: number, index) => i + 1
    );
    const groupData = isEmpty(data)
        ? []
        : days.reduce((acc, date) => {
            const monthData = data.filter((t: any) => {
                const endTime = moment.unix(t?.[0]);

                const tDate = endTime.date();
                return tDate === date;
            });

            // Check empty data to prevent the sumBy from returning 0
            const groupValue = isEmpty(monthData)
                ? undefined
                : sumBy(monthData, (t) => Number(t[1]));

            acc.push(groupValue);
            return acc;
        }, [] as any[]);

    return {
        categories: days,
        data: groupData,
    };
};

export const groupDataByYear = (data: any[]) => {
    const months = Array.from(new Array(12).keys()).map(
        (i: number, index) => i + 1
    );

    const groupData = isEmpty(data)
        ? []
        : months.reduce((acc, month) => {
            const yearData = data.filter((t: any) => {
                const endTime = moment.unix(t?.[0]);

                const tMonth = endTime.format("MM");
                return +tMonth === month;
            });

            // Check empty data to prevent the sumBy from returning 0
            const groupValue = isEmpty(yearData)
                ? undefined
                : sumBy(yearData, (t) => Number(t[1]));

            acc.push(groupValue);
            return acc;
        }, [] as any[]);

    return {
        categories: months.map((m) => moment(m, "M").format("MMM")),
        data: groupData,
    };
};

const calculateTotal = (metricData: MetricData[]): number | null => {
    if (
        isEmpty(metricData) ||
        metricData.every((data) => isEmpty(data.values))
    ) {
        return null;
    }

    return sumBy(metricData, (data: MetricData) => {
        const values = data?.values || [];
        return sumBy(values, (valueEntry: MetricValue) => {
            const numericValue = Number(valueEntry[1]);
            return Number.isNaN(numericValue) ? 0 : numericValue;
        });
    });
};


export const generateChartSeries = ({
    sensorDetails,
    groupBy,
    metricData,
    lastTime,
}: {
    sensorDetails: SensorDetails[];
    groupBy: ConsumptionChartBy;
    metricData: MetricData[];
    lastTime: Moment;
}) => {
    let categories: string[] | number[] = [];
    const series = sensorDetails.map<BarChartSeries>((details) => {
        const rawData = metricData.find(
            (data: MetricData) =>
                data.device_id === details.device_id &&
                data.said === details.said
        );

        let data = [];

        if (rawData) {
            const groupData = groupDataByTime(
                groupBy,
                get(rawData, "values", []),
                lastTime
            );
            data = groupData.data;

            if (!categories?.length) {
                categories = groupData.categories;
            }
        }

        return {
            id: `${details.device_id ?? ""}_${details.said ?? 0}`,
            data,
            type: "bar",
            barWidth: "60%",
            name: details.name,
            color: details.color,
            stack: "Consumption",
        };
    });

    return {
        series,
        categories,
    };
};

export const buildPromQL = ({
    fn, // e.g. "sum", "avg"
    metric, // e.g. "mqtt2tsdb_gauge_na"
    selectors, // array of label objects: [ { gateway: "X" }, { gateway: "Y" } ]
    range, // e.g. "30m"
    resolution, // e.g. "5s"
}: {
    fn?: string;
    metric: string;
    selectors: Array<{ [key: string]: string | number }>;
    range?: string;
    resolution?: string;
}) => {
    // convert labels to PromQL {key="value"} form
    const toLabelStr = (labels: { [key: string]: string | number }) => {
        const str = Object.entries(labels)
            .map(([k, v]) => `${k}="${String(v)}"`)
            .join(",");
        return `{${str}}`;
    };

    // join multiple selectors with " or "
    const innerExpr = selectors
        .map((lbl) => `${metric}${toLabelStr(lbl)}`)
        .join(" or ");

    // wrap with function
    let expr = fn ? `${fn}(${innerExpr})` : innerExpr;

    // append [range:resolution] or [range]
    if (range) {
        expr += resolution ? `[${range}:${resolution}]` : `[${range}]`;
    }

    return expr;
};

const getCombinedMetricData = async ({
    query,
    time_range,
    gatewayIds,
    isRawData,
    lastTimestamp,
    timeValue,
    sensorReportRate,
}: {
    query: string;
    chartColor: any;
    sensorType: string;
    time_range: string;
    query_time: QueryTime;
    name: string;
    gatewayIds?: string[];
    isRawData: boolean;
    lastTimestamp: string | number;
    timeValue: number;
    sensorReportRate: number;
}) => {
    const mttqQuery = getMQTTquery({
        queryType: isRawData ? "raw" : "aggregated",
        query,
        time_range,
        lastTimestamp,
        timeValue,
        numberOfChart: 1,
        isCombinedChart: true,
        sensorReportRate,
    });

    const queryData = mttqQuery.body;
    const params: any = {
        time: queryData.time,
    };

    if (gatewayIds?.length) {
        params.query = buildPromQL({
            fn: "sum",
            metric: queryData.time_series,
            selectors: gatewayIds.map((gatewayId: string) => ({
                gateway: gatewayId,
            })),
            range: queryData.time_range,
            resolution: queryData.resolution,
        });
    }

    return fetchMetricData(params);
};

export const getSampleQuery = async ({
    query,
    chartColor,
    sensorType,
    gapTime,
    time_range,
    query_time,
    name,
    isCombinedChart = false,
    gatewayIds,
}: {
    query: string;
    chartColor: any;
    sensorType: string;
    gapTime: number;
    time_range: string;
    query_time: QueryTime;
    name: string;
    isCombinedChart?: boolean;
    gatewayIds?: string[];
}) => {
    let response: any;
    let minResponse: any;
    let maxResponse: any;

    const lastTimestamp = query_time ?? moment().unix();
    const timeValue = convertPrometheusTimeToSeconds(time_range);
    const sensorReportRate: number = gapTime / 1000;
    const totalSample = Math.floor(timeValue / sensorReportRate);

    const isRawData =
        totalDoesNotExceedMax({ total: totalSample, numberOfChart: 1 }) &&
        timeValue <= monthsToSeconds(6);

    let prepareAggregated = false;
    if (isCombinedChart) {
        response = await getCombinedMetricData({
            query,
            chartColor,
            sensorType,
            time_range,
            query_time,
            name,
            gatewayIds,
            isRawData,
            lastTimestamp,
            timeValue,
            sensorReportRate,
        });

        gapTime =
            convertPrometheusTimeToSeconds(
                getAggregateBy({ timeValue, numberOfChart: 1 })
            ) * 1000;
    } else if (isRawData) {
        try {
            const mttqQuery = getMQTTquery({
                queryType: "raw",
                query,
                time_range,
                lastTimestamp,
                timeValue,
                numberOfChart: 1,
                isCombinedChart,
            });

            response = await fetchMetricData(mttqQuery.body);
        } catch (err) {
            // TO BE ADDED: Handle error obj
            return err;
        }
    } else {
        prepareAggregated = true;
        gapTime =
            convertPrometheusTimeToSeconds(
                getAggregateBy({ timeValue, numberOfChart: 1 })
            ) * 1000;

        [response, minResponse, maxResponse] = await Promise.all(
            ["avg", "min", "max"].map((type) => {
                const mttqQuery = getMQTTquery({
                    queryType: "aggregated",
                    query,
                    time_range,
                    lastTimestamp,
                    timeValue,
                    aggregationType: type,
                    numberOfChart: 1,
                    isCombinedChart,
                });

                return fetchMetricData(mttqQuery.body);
            })
        );
    }

    return _getSampleQuery({
        response,
        gapTime,
        chartColor,
        timeValue,
        prepareAggregated,
        minResponse,
        maxResponse,
        sensorType,
        name,
    });
};

const _getStackedChartQuery = ({
    panelQueries,
    time_range,
    query_time,
    gapTime,
}: {
    panelQueries: string[];
    time_range: string;
    query_time: QueryTime;
    gapTime: number;
}) => {
    const lastTimestamp = query_time ?? moment().unix();
    const timeValue = convertPrometheusTimeToSeconds(time_range);

    const sensorReportRate: number = gapTime / 1000;
    const totalSample = Math.floor(timeValue / sensorReportRate);

    const queries: any = [];
    for (const query of panelQueries) {
        if (
            totalDoesNotExceedMax({
                total: totalSample,
                numberOfChart: panelQueries.length,
            })
        ) {
            const mttqQuery = getMQTTquery({
                queryType: "raw",
                query: query,
                time_range,
                lastTimestamp,
                timeValue,
                numberOfChart: panelQueries.length,
            });
            queries.push(mttqQuery.body);
        } else {
            const mttqQuery = getMQTTquery({
                queryType: "aggregated",
                query: query,
                time_range,
                lastTimestamp,
                timeValue,
                aggregationType: "avg",
                numberOfChart: panelQueries.length,
            });
            queries.push(mttqQuery.body);
        }
    }

    return queries;
};

export const getStackedChartQuery = async ({
    panels,
    namesAry,
    yAxisAry,
    time_range,
    query_time,
    gapTime,
}: {
    panels: any;
    namesAry: any;
    yAxisAry: any;
    time_range: string;
    query_time: QueryTime;
    gapTime: number;
}) => {
    const timeValue = convertPrometheusTimeToSeconds(time_range);
    const sensorReportRate: number = gapTime / 1000;
    const totalSample = Math.floor(timeValue / sensorReportRate);

    const newQueries: any = _getStackedChartQuery({
        panelQueries: panels.queries,
        time_range,
        query_time,
        gapTime,
    });

    let queryRes: any = [];
    try {
        queryRes = await Promise.all(
            newQueries.map((i: any) => fetchMetricData(i))
        );
    } catch (err) {
        return err;
    }

    let series: any = [];
    for (let h = 0; h < queryRes.length; h++) {
        const isRawData = totalDoesNotExceedMax({
            total: totalSample,
            numberOfChart: queryRes.length,
        });

        let result: any;
        if (isRawData) {
            result = queryRes[h].data.data.result;
        } else {
            combineResultArrays(queryRes[h]);
            result = [
                changeDataTo2DP(queryRes[h].data.data.result[0], "STACKED"),
            ];
        }

        const thisIndex = yAxisAry.findIndex(
            (info: any) => info.sensorType === panels.attributes[h]?.name
        );

        let dataResult = [];
        for (const element of result) {
            for (const value of element.values) {
                value[0] = value[0] * 1000;
            }
            dataResult = element.values;
        }

        const sample = {
            name: namesAry[h] ? `${namesAry[h]}` : "",
            type: "line",
            itemStyle: {
                color: panels.attributes[h]?.color,
            },
            yAxisIndex: thisIndex === -1 ? 0 : thisIndex,
            lineStyle: {
                type: thisIndex === 0 ? "solid" : [10, 2],
            },
            data: dataResult,
            smooth: true,
            showSymbol: false,
            info: get(queryRes[h], `data.data.result[0].metric`, {}),
        };

        series = [...series, sample];
    }

    return { series };
};

export const getOnlineOfflineValue = async (
    gateway_id: string,
    fromTime: number,
    toTime?: number
) => {
    try {
        const endTime = toTime ?? moment().unix();
        const duration = convertSecondsToPrometheusTime(endTime - fromTime);
        const query = `sum(count_over_time(mqtt2tsdb_heartbeat_na{gateway="${gateway_id}"}[${duration}]))`;

        return fetchMetricData({
            query,
            time: endTime,
        });
    } catch (err) {
        return err;
    }
};

type ConsumptionMetricInput = {
    sensorDetails: SensorDetails[];
    queryTime: number;
    groupBy: ConsumptionChartBy;
    sensors: any[];
    queries: string[];
};

const metricDataFromQueryResults = (
    results: QueryResult[],
    filter: MetricValueFilter,
    groupBy?: ConsumptionChartBy
): MetricData[] => {
    return results.map((result) => {
        const device_id = get(result, "metric.uid").toString();
        const gateway_id = get(result, "metric.gateway").toString();
        const said = Number.parseInt(get(result, "metric.said"));

        /**
         * Apply time offset for consumption charts based on groupBy period:
         *
         * DAILY: Subtract 1h only
         * - Backend data points are hourly aggregates with end timestamps
         * - Subtract 1h to align with chart display
         *
         * WEEKLY/MONTHLY/YEARLY: Subtract 24h + timezone offset
         * - increase(24h) returns end time of 24h period (next day at timezone offset time)
         * - Subtract 24h (period duration) + timezone offset = proper alignment
         * - Converts "31st July 7am" → "30th July 12am" for proper day grouping
         *
         * Ex: Backend [31st July 7am, value] → Display [30th July, value]
         */
        result.values = get(result, "values", []).map((value) => {
            const timezoneOffset = getTimezoneOffset();
            const hoursToSubtract =
                groupBy === ConsumptionChartBy.Day || !groupBy
                    ? 1
                    : 24 + timezoneOffset;
            const originalTimestamp = value[0];
            const timestamp = moment
                .unix(originalTimestamp)
                .subtract(hoursToSubtract, "h")
                .unix();

            return [timestamp, value[1]];
        });

        const values = (result.values || []).filter((value) =>
            isMetricValueTimestampInFilter(value[0], filter)
        );

        return {
            values,
            device_id,
            gateway_id,
            said,
        };
    });
};

const getSingleConsumptionMetricData = async ({
    sensorDetails,
    groupBy,
    queryTime,
    query,
}: {
    sensorDetails: SensorDetails;
    groupBy: ConsumptionChartBy;
    queryTime: number;
    query: string;
}): Promise<MetricDataForComparison> => {
    const timeUnit = timeUnitMapping[groupBy];

    const { time, previousTime } = getCurrentAndPreviousTime(
        queryTime,
        timeUnit
    );

    const getMetricData = async (time: Moment): Promise<MetricData[]> => {
        const { start, end } = _getConsumptionQueryTime(time, timeUnit);

        // Define consumption query configuration based on time period
        const queryConfig = {
            [ConsumptionChartBy.Day]: { interval: "1h", range: "[24h:1h]" },
            [ConsumptionChartBy.Week]: { interval: "1d", range: "[7d:1d]" },
            [ConsumptionChartBy.Month]: {
                interval: "1d",
                range: `[${time.daysInMonth()}d:1d]`,
            },
            [ConsumptionChartBy.Year]: {
                interval: "1d",
                range: `[${moment(time).endOf("year").dayOfYear()}d:1d]`,
            },
        };

        const config =
            queryConfig[groupBy] || queryConfig[ConsumptionChartBy.Day];

        const timezoneOffset = getTimezoneOffset();
        const offsetString = requiresTimezoneOffset(groupBy)
            ? ` offset ${timezoneOffset}h`
            : "";
        const wsQuery = `delta(${consumptionQuery(query)}[${config.interval
            }]${offsetString})${config.range}`;

        const queryTime = requiresTimezoneOffset(groupBy)
            ? getCombinedConsumptionQueryTime(time.unix(), timeUnit)
            : end.unix();

        const params = {
            query: wsQuery,
            time: queryTime,
            originQuery: query,
        };

        const response = await fetchMetricData(params);

        const transformedResults = metricDataFromQueryResults(
            get(response, "data.data.result", []),
            { onOrAfter: start, onOrBefore: end },
            groupBy
        ).filter(
            (metricData) =>
                metricData.device_id === sensorDetails.device_id &&
                metricData.said === sensorDetails.said
        );

        return transformedResults;
    };

    const [metricData, previousMetricData] = await Promise.all([
        getMetricData(time),
        getMetricData(previousTime),
    ]);

    return { metricData, previousMetricData };
};

const _getConsumptionQueryTime = (
    endTime: Moment,
    timeUnit: unitOfTime.Base
) => {
    const endOfUnit = moment(endTime).endOf(timeUnit).toDate();
    let start = moment(endOfUnit).startOf(timeUnit);

    /**
     * BE requirement
     * Prometheus does not return the data point at the exact startTime, it return > startTime and <= endTime
     * So we need to subtract 1 second to make sure the data point at the startTime is included in the query result
     **/
    start = start.subtract(1, "second");

    let end = moment(roundTimestamp(endOfUnit, 60 * 60) * 1000);

    const timeRange = convertSecondsToPrometheusTime(end.diff(start, "second"));

    return {
        start,
        end,
        timeRange,
    };
};

/**
 * Build WebSocket query for combined consumption charts
 */
const buildCombinedConsumptionWSQuery = (
    metricType: MetricType,
    sensorDetails: SensorDetails[],
    timeUnit: unitOfTime.Base,
    queryTime: number
): string => {
    const metricQuery = CombinedConsumptionQuery[metricType];
    const timezoneOffset = getTimezoneOffset();
    const time = moment.unix(queryTime);

    // Build individual sensor queries
    const sensorQueries = sensorDetails.map(
        ({ gateway_id, device_id, said }) => {
            return `delta(${metricQuery}{gateway="${gateway_id}", uid="${device_id}", said="${said}"}[${getIncreaseInterval(
                timeUnit
            )}]${getOffsetString(timeUnit, timezoneOffset)})`;
        }
    );

    // Combine queries with OR operator and add time range
    const combinedQuery = `(${sensorQueries.join(" or ")})${getTimeRange(
        timeUnit,
        time
    )}`;

    return combinedQuery;
};

/**
 * Get the delta interval based on time unit
 */
const getIncreaseInterval = (timeUnit: unitOfTime.Base): string => {
    switch (timeUnit) {
        case "day":
            return "1h";
        case "week":
        case "month":
        case "year":
            return "1d";
        default:
            return "1h";
    }
};

/**
 * Get the offset string for the query
 */
const getOffsetString = (
    timeUnit: unitOfTime.Base,
    vnOffset: number
): string => {
    switch (timeUnit) {
        case "day":
            return "";
        case "week":
        case "month":
        case "year":
            return ` offset ${vnOffset}h`;
        default:
            return "";
    }
};

/**
 * Get the time range for the query
 */
export const getTimeRange = (timeUnit: unitOfTime.Base, time?: Moment): string => {
    switch (timeUnit) {
        case "day":
            return "[1d:1h]";
        case "week":
            return "[7d:1d]";
        case "month": {
            const daysInMonth = time ? time.daysInMonth() : 31;
            return `[${daysInMonth}d:1d]`;
        }
        case "year": {
            const daysInYear = time?.isLeapYear() ? 366 : 365;
            return `[${daysInYear}d:1d]`;
        }
        default:
            return "[1d:1h]";
    }
};

/**
 * Get the query time with proper timezone handling
 */
export const getCombinedConsumptionQueryTime = (
    queryTime: number,
    timeUnit: unitOfTime.Base
): number => {
    const timezoneOffset = getTimezoneOffset();
    const time = moment.unix(queryTime);

    switch (timeUnit) {
        case "day":
            // For daily: use start of next day (00:00:00) instead of end of current day (23:59:59)
            return time.add(1, "day").startOf("day").unix();
        case "week":
            // For weekly: convert local time to UTC by adding timezone offset
            return time
                .endOf("week") // Saturday 23:59:59
                .startOf("day") // Saturday 00:00:00
                .add(1, "day") // Sunday 00:00:00 (next Sunday)
                .add(timezoneOffset, "hours") // Shift timezone because the prometheus considers this unix time as the time of the local
                .unix();
        case "month": {
            // For monthly: use start of NEXT month to capture complete current month data
            const startOfNextMonth = time
                .add(1, "month")
                .startOf("month")
                .startOf("day");
            return startOfNextMonth.add(timezoneOffset, "hours").unix();
        }
        case "year": {
            // For yearly: use start of NEXT year to capture complete current year data
            const startOfNextYear = time
                .add(1, "year")
                .startOf("year")
                .startOf("day");
            return startOfNextYear.add(timezoneOffset, "hours").unix();
        }
        default:
            return queryTime;
    }
};

export const getMultipleConsumptionMetricData = async ({
    metricType,
    sensorDetails,
    queryTime,
    timeUnit,
}: {
    metricType: MetricType;
    sensorDetails: SensorDetails[];
    queryTime: number;
    timeUnit: unitOfTime.Base;
}): Promise<MetricDataForComparison> => {
    const { time, previousTime } = getCurrentAndPreviousTime(
        queryTime,
        timeUnit
    );

    const getMetricData = async (time: Moment): Promise<MetricData[]> => {
        const { start, end } = _getConsumptionQueryTime(time, timeUnit);

        const wsQuery = buildCombinedConsumptionWSQuery(
            metricType,
            sensorDetails,
            timeUnit,
            time.unix()
        );

        const wsQueryTime = getCombinedConsumptionQueryTime(
            time.unix(),
            timeUnit
        );

        const params = {
            query: wsQuery,
            time: wsQueryTime,
            originQuery: wsQuery,
        };

        const response = await fetchMetricData(params);

        const filteredResults = metricDataFromQueryResults(
            get(response, "data.data.result", []),
            { onOrAfter: start, onOrBefore: end },
            getConsumptionChartByFromTimeUnit(timeUnit)
        );

        return filteredResults;
    };

    const [metricData, previousMetricData] = await Promise.all([
        getMetricData(time),
        getMetricData(previousTime),
    ]);

    return {
        metricData,
        previousMetricData,
    };
};

/**
 * V3 consumption query metrics using pre-aggregated 15-minute data
 */
const V3ConsumptionQuery: Record<MetricType, string> = {
    waterflow: "last_15m_flowvolume_m3",
    energy: "last_15m_activeenergy_kwh",
};

/**
 * Builds a V3 PromQL `last_over_time` sub-query range vector and its query time.
 *
 * Generated query format per groupBy period:
 *  - Day:             `last_over_time(metric{...}[1h])[<T>:1h]`           (no offset)
 *  - Week/Month/Year: `last_over_time(metric{...}[1d] offset Xh)[<T>:1d]` (timezone offset applied)
 *
 * Returns the PromQL query string and the adjusted endTime (provided endTime + 1s)
 * to be used as the Prometheus query evaluation time.
 */
export const buildV3ConsumptionQuery = (
    metric: string,
    sensorDetails: SensorDetails,
    groupBy: ConsumptionChartBy,
    endTime: Moment
): { query: string; endTime: Moment } => {
    const { gateway_id, device_id, said } = sensorDetails;
    const selector = `${metric}{gateway="${gateway_id}", uid="${device_id}", said="${said}"}`;
    const timezoneOffset = getTimezoneOffset();
    const timeUnit = timeUnitMapping[groupBy];

    const { start, end } = _getConsumptionQueryTime(endTime, timeUnit);
    const promInterval = groupBy === ConsumptionChartBy.Day ? "1h" : "1d";
    let innerExpr = `${selector}[${promInterval}]`;
    let queryTime = moment(end);

    if (promInterval === "1d") {
        // For daily intervals, we need to apply timezone offset to align with local day boundaries
        innerExpr += ` offset ${timezoneOffset}h`;

        // BE requirement: Plus timezone offset because Prometheus will evaluate the query at the provided queryTime as local time, so we need to shift it to match the actual data points which are aggregated based on local time
        queryTime = queryTime.add(timezoneOffset, "hours");
    }

    const duration = convertSecondsToPrometheusTime(
        queryTime.unix() - start.unix()
    );
    const promTimeRange = `[${duration}:${promInterval}]`;

    const query = `last_over_time(${innerExpr})${promTimeRange}`;
    return { query, endTime: queryTime };
};

const computeConsumptionBarValues = (
    cumulativeValues: MetricValue[],
    groupBy: ConsumptionChartBy
): MetricValue[] => {
    if (cumulativeValues.length < 2) return [];

    const sorted = [...cumulativeValues].sort((a, b) => a[0] - b[0]);

    const slotKey = (unixTs: number): string => {
        const m = moment.unix(unixTs);
        if (groupBy === ConsumptionChartBy.Day) return m.format("YYYY-MM-DD-HH");
        if (groupBy === ConsumptionChartBy.Year) return m.format("YYYY-MM");
        return m.format("YYYY-MM-DD"); // Week / Month
    };

    /**
     * For Year view: bar per month = first_value_of_next_month - first_value_of_current_month.
     * If either month's first reading is missing, that bar is skipped.
     * Exception
     * For the last month from payload, could not get value of next month to calculate the consumption.
     * So using last known value − first value
     */
    if (groupBy === ConsumptionChartBy.Year) {
        const firstByMonth = new Map<string, MetricValue>();

        const lastByMonth = new Map<string, MetricValue>();
        for (const point of sorted) {
            const key = slotKey(point[0]);
            if (!firstByMonth.has(key)) {
                firstByMonth.set(key, point);
            }
            lastByMonth.set(key, point); // always overwrite → keeps the latest
        }

        const barValues: MetricValue[] = [];
        firstByMonth.forEach((currentFirst, key) => {
            const nextMonthKey = moment(key, "YYYY-MM").add(1, "month").format("YYYY-MM");
            const nextFirst = firstByMonth.get(nextMonthKey);
            if (nextFirst) {
                // Normal months: next month's first − this month's first
                barValues.push([currentFirst[0], String(Number(nextFirst[1]) - Number(currentFirst[1]))]);
            } else {
                // Last month in the payload: fall back to last known value − first value
                const currentLast = lastByMonth.get(key);
                if (currentLast && currentLast[0] !== currentFirst[0]) {
                    barValues.push([currentFirst[0], String(Number(currentLast[1]) - Number(currentFirst[1]))]);
                }
            }
        });
        return barValues;
    }

    // For Day / Week / Month: one reading per slot, use consecutive-pair approach.
    // Returns the slot key immediately following the slot containing unixTs.
    const nextSlotKey = (unixTs: number): string => {
        const m = moment.unix(unixTs);
        if (groupBy === ConsumptionChartBy.Day) return m.add(1, "hour").format("YYYY-MM-DD-HH");
        return m.add(1, "day").format("YYYY-MM-DD"); // Week / Month
    };

    const barValues: MetricValue[] = [];
    let i = 0;
    for (let j = 1; j < sorted.length; j++) {
        // Only emit a bar when the two points are in adjacent slots (no gap between them)
        if (slotKey(sorted[j][0]) === nextSlotKey(sorted[i][0])) {
            barValues.push([sorted[i][0], String(Number(sorted[j][1]) - Number(sorted[i][1]))]);
        }
        i = j;
    }

    return barValues;
};

export const getSingleConsumptionMetricDataV3 = async ({
    sensorDetails,
    groupBy,
    endTime,
    metricType,
}: {
    sensorDetails: SensorDetails;
    groupBy: ConsumptionChartBy;
    endTime: number;
    metricType: MetricType;
}): Promise<{
    metricData: MetricData[];
    previousMetricData: MetricData[];
    cumulativeValues: MetricValue[];
    previousCumulativeValues: MetricValue[];
}> => {
    const timeUnit = timeUnitMapping[groupBy];
    const { time: currentTime, previousTime } = getCurrentAndPreviousTime(
        endTime,
        timeUnit
    );
    const metric = V3ConsumptionQuery[metricType];

    const getMetricData = async (
        endTime: Moment
    ): Promise<{ barData: MetricData[]; cumulativeValues: MetricValue[] }> => {
        const { query, endTime: qt } = buildV3ConsumptionQuery(
            metric,
            sensorDetails,
            groupBy,
            endTime
        );

        const response = await fetchMetricData({
            query,
            time: qt.unix(),
        });

        const results: QueryResult[] = get(response, "data.data.result", []);

        const matchingResult = results.find(
            (r) =>
                get(r, "metric.uid").toString() === sensorDetails.device_id &&
                Number.parseInt(get(r, "metric.said")) === sensorDetails.said
        );

        if (!matchingResult) {
            return {
                barData: [
                    {
                        device_id: sensorDetails.device_id,
                        gateway_id: sensorDetails.gateway_id,
                        said: sensorDetails.said,
                        values: [],
                    },
                ],
                cumulativeValues: [],
            };
        }

        const cumulativeValues: MetricValue[] = get(
            matchingResult,
            "values",
            []
        );
        const barValues = computeConsumptionBarValues(cumulativeValues, groupBy);

        return {
            barData: [
                {
                    device_id: get(matchingResult, "metric.uid").toString(),
                    gateway_id: get(
                        matchingResult,
                        "metric.gateway"
                    ).toString(),
                    said: Number.parseInt(get(matchingResult, "metric.said")),
                    values: barValues,
                },
            ],
            cumulativeValues,
        };
    };

    const [current, previous] = await Promise.all([
        getMetricData(currentTime),
        getMetricData(previousTime),
    ]);

    return {
        metricData: current.barData,
        previousMetricData: previous.barData,
        cumulativeValues: current.cumulativeValues,
        previousCumulativeValues: previous.cumulativeValues,
    };
};

/**
 * Fetches consumption data using V3 queries (last_over_time with pre-aggregated 15-minute metrics).
 * Calculates bar values on the frontend by computing differences between consecutive cumulative data points.
 */
export const getConsumptionMetricV3 = async ({
    sensorDetails,
    queryTime,
    groupBy,
    sensors,
}: ConsumptionMetricInput): Promise<ConsumptionChartData> => {
    const metricType: MetricType =
        getSensorType(sensors[0]) === SensorType.WaterConsumption
            ? "waterflow"
            : "energy";

    const results = await Promise.all(
        sensorDetails.map((details) =>
            getSingleConsumptionMetricDataV3({
                sensorDetails: details,
                groupBy,
                endTime: queryTime,
                metricType,
            })
        )
    );

    const metricData = results.flatMap((r) => r.metricData);
    const previousMetricData = results.flatMap((r) => r.previousMetricData);

    const { series, categories } = generateChartSeries({
        sensorDetails,
        metricData,
        lastTime: moment.unix(queryTime),
        groupBy,
    });

    const total = calculateTotal(metricData);
    const previousTotal = calculateTotal(previousMetricData);

    return {
        categories,
        series,
        total,
        previousTotal,
    };
}

/**
 * Fetches consumption data to be used in consumption charts.
 * @deprecated Use getConsumptionMetricV3 instead which uses new backend queries with better timezone handling.
 */
export const getConsumptionMetricV2 = async ({
    sensorDetails,
    queryTime,
    groupBy,
    queries,
    sensors,
}: ConsumptionMetricInput): Promise<ConsumptionChartData> => {
    const isSingle = sensorDetails?.length === 1;
    const lastTime = moment.unix(queryTime);

    const metricType: MetricType =
        getSensorType(sensors[0]) === SensorType.WaterConsumption
            ? "waterflow"
            : "energy";
    const { metricData, previousMetricData } = isSingle
        ? await getSingleConsumptionMetricData({
            sensorDetails: sensorDetails[0],
            groupBy,
            queryTime,
            query: queries[0],
        })
        : await getMultipleConsumptionMetricData({
            metricType,
            sensorDetails,
            queryTime,
            timeUnit: timeUnitMapping[groupBy],
        });

    const { series, categories } = generateChartSeries({
        sensorDetails,
        metricData,
        lastTime,
        groupBy,
    });

    return {
        categories,
        series,
        total: calculateTotal(metricData),
        previousTotal: calculateTotal(previousMetricData),
    };
};

/**
 * Get metrics for card with multiple charts inside.
 * @param queries The queries to fetch metrics for.
 */
export const getCardMetric = async ({ queries }: { queries: string[] }) => {
    const lastTimestamp = moment().unix();
    const promiseQueries = queries.map((q) => {
        const query = `${q}[1d:5m]`;
        return fetchMetricData({
            query: query,
            time: lastTimestamp,
        });
    });

    try {
        const queryRes = await Promise.all(promiseQueries);

        const metric = queryRes.map((res) => {
            const values = get(res, `data.data.result[0].values`, []);
            const info = get(res, `data.data.result[0].metric`, null);

            if (!info) return null;

            return {
                gatewayId: info.gateway as string,
                ldsuUId: info.uid as string,
                said: Number(info.said),
                value: values
                    ? (values.map((v: any) => [v[0] * 1000, v[1]]) as [
                          number,
                          number
                      ][])
                    : ([] as [number, number][]),
            };
        });

        return metric.filter((m): m is NonNullable<typeof m> => m !== null);
    } catch {
        return [];
    }
};

export type CardSensorReading = {
    said: number;
    highest: number | null;
    lowest: number | null;
    lastActive: number | null;
    value: number | null;
};

/**
 * Fetches the latest sensor readings for all sensors on one LDSU device and
 * applies binary (0/1) conversion for sensors identified as binary.
 * Shared by LDSU_CARD_READINGS and LDSU_CARD_OVERVIEW_READINGS widgets.
 */
export const getCardSensorReadings = async ({
    gatewayId,
    deviceId,
    devices,
}: {
    gatewayId: string;
    deviceId: string;
    devices: DeviceInfo[];
}): Promise<CardSensorReading[] | null> => {
    const { data, status } = await getSensorLastReading(gatewayId, deviceId);
    if (!isHttpSuccess(status)) return null;
    return devices.map((d) => {
        const raw = toFiniteReadingNumber(data?.value?.[d.said]);
        return {
            said: d.said,
            highest: toFiniteReadingNumber(data?.max?.[d.said]),
            lowest: toFiniteReadingNumber(data?.min?.[d.said]),
            lastActive: toFiniteReadingNumber(data?.ts),
            value: raw !== null && isBinarySensor(d.cls, d.format) ? toBinaryValue(raw) : raw,
        };
    });
};

/**
 * Fetches the latest reading for a single sensor and applies binary (0/1)
 * conversion when the sensor is identified as binary.
 * Used by SENSOR_CARD_MULTI_VALUES widgets where each sensor may be on a
 * different gateway/device.
 */
export const getCardSensorReading = async ({
    gatewayId,
    deviceId,
    said,
    cls,
    format,
}: {
    gatewayId: string;
    deviceId: string;
    said: number;
    cls: string;
    format?: string;
}): Promise<(CardSensorReading & { gatewayId: string; deviceId: string }) | null> => {
    const { data, status } = await getSensorLastReading(gatewayId, deviceId);
    if (!isHttpSuccess(status)) return null;
    const raw = toFiniteReadingNumber(data?.value?.[said]);
    return {
        gatewayId,
        deviceId,
        said,
        highest: toFiniteReadingNumber(data?.max?.[said]),
        lowest: toFiniteReadingNumber(data?.min?.[said]),
        lastActive: toFiniteReadingNumber(data?.ts),
        value: raw !== null && isBinarySensor(cls, format) ? toBinaryValue(raw) : raw,
    };
};

export const fetchSensorMetricData = async ({
    metric,
    gatewayId,
    deviceId,
    said,
    startTime,
    endTime,
    previousValues = [],
}: {
    metric: string;
    gatewayId: string;
    deviceId: string;
    said: string | number;
    startTime: number;
    endTime: number;
    previousValues?: [number, string][];
}): Promise<[number, string][] | null> => {
    // Only fetch data we don't already have
    const lastKnownTimestamp = previousValues.at(-1)?.[0] ?? null;
    let fetchFrom =
        lastKnownTimestamp !== null && lastKnownTimestamp > startTime
            ? lastKnownTimestamp
            : startTime;

    // Subtract 1s so Prometheus's exclusive lower bound (start, end] includes the exact startTime
    fetchFrom = fetchFrom - 1;

    const range = convertSecondsToPrometheusTime(endTime - fetchFrom);
    const metricWithLabels = `${metric}{gateway="${gatewayId}", uid="${deviceId}", said="${said}"}`;
    const query = `${metricWithLabels}[${range}]`;

    try {
        const response = await fetchMetricData({
            query,
            time: endTime,
        });

        const newValues: [number, string][] = get(
            response,
            "data.data.result[0].values",
            []
        );

        // Merge previous + new, deduplicate by timestamp (new wins), trim to [startTime, endTime]
        const merged = new Map<number, string>();
        for (const [ts, val] of [...previousValues, ...newValues]) {
            merged.set(ts, val);
        }
        return (Array.from(merged.entries()) as [number, string][])
            .filter(([ts]) => ts >= startTime && ts <= endTime)
            .sort(([a], [b]) => a - b);
    } catch {
        return null;
    }
};

export const getConsumptionChartTotal_ForDay = async (
    queries: string[],
    queryTime: number
): Promise<number | null> => {
    try {
        const time = queryTime ? moment.unix(queryTime) : moment();
        // Get local midnight for the current day
        const localMidnight = time.startOf("day").add(1, "day").unix();

        const wsQuery = queries
            .map((query) => `delta(${consumptionQuery(query)}[1d])`)
            .join(" or ");

        const params = {
            query: wsQuery,
            time: localMidnight,
        };

        const response = await fetchMetricData(params);
        const results = get(response, "data.data.result", []);

        if (results.length > 0) {
            const latestValue = results.reduce((sum: number, result: any) => {
                return sum + Number(addDecimalPlace(result.value[1], 4));
            }, 0);
            const numericValue = Number(latestValue);
            return Number.isNaN(numericValue) ? null : numericValue;
        }

        return null;
    } catch (error) {
        console.error("Error fetching consumption total:", error);
        return null;
    }
};

export const fetchPSLMetric = async (query: string) => {
    try {
        const status = await fetchMetricData({
            query: query,
        });

        return get(status, "data.data.result") as Array<any> ?? [];
    } catch {
        return [];
    }
}


export const fetchPSLStatus = async (
    devices: {
        gateway_id: string,
        device_id: string,
        said: string;
    }[]
) => {
    const queries = devices.map(({ gateway_id, device_id, said }: any) => `mqtt2tsdb_digitalinput_na{gateway="${gateway_id}",uid="${device_id}",said="${said}"}`).join(" or ");

    try {
        const result = await fetchPSLMetric(
            queries
        );

        let metric = result.map((item) => {
            const { gateway: gatewayId, uid: deviceId } = get(item, "metric");
            const data: PSLStatus = {
                gatewayId,
                deviceId,
                status: Number(get(item, "value[1]"))
            }
            return data;
        });

        return metric;
    } catch {
        return [] as PSLStatus[];
    }
}

function sumArrays(
    arrays: (number | null | undefined)[][]
): (number | undefined)[] {
    if (arrays.length === 0) {
        return [];
    }

    const maxLength = arrays.reduce(
        (max, array) => Math.max(max, array.length),
        0
    );

    return Array.from({ length: maxLength }, (_, index) => {
        let sum = 0;
        let hasNumber = false;

        for (const arr of arrays) {
            const value = arr[index];

            if (typeof value === "number") {
                sum += value;
                hasNumber = true;
            }
        }

        return hasNumber ? sum : undefined;
    });
}

/** Bar/raw values from `avg_15m_digitalinput_na` usage queries represent 15-minute slots */
export const PSL_USAGE_AVG_SAMPLE_MINUTES = 15;
export const PSL_USAGE_AVG_SAMPLE_SECONDS =
    PSL_USAGE_AVG_SAMPLE_MINUTES * 60;

const getPSLUsageSubqueryRange = (
    groupBy: ConsumptionChartBy,
    endTime: Moment
): string => {
    switch (groupBy) {
        case ConsumptionChartBy.Day:
            return "[1d1s:1h]";
        case ConsumptionChartBy.Week:
            return "[7d1s:1d]";
        case ConsumptionChartBy.Month:
            return `[${endTime.daysInMonth()}d1s:1d]`;
        case ConsumptionChartBy.Year:
            return `[${endTime.isLeapYear() ? 366 : 365}d1s:1d]`;
        default:
            return "[1d1s:1h]";
    }
};

// This function very similar with metricDataFromQueryResults
// The only different is in Daily groupBy, it does not subtract an extra hour
const pslMetricDataFromQueryResults = (
    results: QueryResult[],
    filter: MetricValueFilter,
    groupBy: ConsumptionChartBy
): MetricData[] => {
    return results.map((result) => {
        const device_id = get(result, "metric.uid").toString();
        const gateway_id = get(result, "metric.gateway").toString();
        const said = Number.parseInt(get(result, "metric.said"));

        /**
         * PSL avg_15m usage queries:
         *
         * DAILY: Do not subtract an extra hour — PromQL uses `[1h] offset -1h` so timestamps
         * already align to hour-start bars.
         *
         * WEEKLY/MONTHLY/YEARLY: Subtract 24h + timezone offset (same calendar alignment as
         * metricDataFromQueryResults for non-day periods).
         */
        const shiftedValues = get(result, "values", []).map((value: MetricValue) => {
            if (groupBy === ConsumptionChartBy.Day) {
                return [value[0], value[1]] as MetricValue;
            }
            const timezoneOffset = getTimezoneOffset();
            const hoursToSubtract = 24 + timezoneOffset;
            const timestamp = moment
                .unix(value[0])
                .subtract(hoursToSubtract, "h")
                .unix();
            return [timestamp, value[1]] as MetricValue;
        });

        const values = shiftedValues.filter((value) =>
            isMetricValueTimestampInFilter(value[0], filter)
        );

        return {
            values,
            device_id,
            gateway_id,
            said,
        };
    });
};


/** Calculate total seconds for PSL usage (raw values × 15 min per slot) */
const calculatePSLUsageTotalSeconds = (metricData: MetricData[]): number => {
    let totalSeconds = 0;

    for (const data of metricData) {
        const values = data.values || [];
        totalSeconds +=
            sumBy(values, (valueEntry: MetricValue) => {
                const numericValue = Number(valueEntry[1]);
                return Number.isNaN(numericValue) ? 0 : numericValue;
            }) * PSL_USAGE_AVG_SAMPLE_SECONDS;
    }

    return totalSeconds;
};

export const getPSLUsageMetricData = async (
    {
        devices,
        groupBy,
        queryTime,
        batchSize = 5,
    }: {
        devices: SensorDetails[];
        groupBy: ConsumptionChartBy;
        queryTime: number;
        /** Max devices per Prometheus query. Devices are split into batches fired in parallel — avoids oversized PromQL expressions being rejected by the server. Defaults to 1 (one query per device). */
        batchSize?: number;
    }
) => {
    const endTime = moment.unix(queryTime);
    const timeUnit = timeUnitMapping[groupBy];
    const { start, end } = _getConsumptionQueryTime(endTime, timeUnit);
    const timezoneOffset = getTimezoneOffset();
    const subqueryRange = getPSLUsageSubqueryRange(groupBy, endTime);
    const wsQueryTime = getCombinedConsumptionQueryTime(
        endTime.unix(),
        timeUnit
    );

    const fetchBatch = async (batch: SensorDetails[]): Promise<MetricData[]> => {
        const innerExprs = batch.map(({ gateway_id, device_id, said }) => {
            const saidStr = String(said);
            const sel = `avg_15m_digitalinput_na{gateway="${gateway_id}",uid="${device_id}",said="${saidStr}"}`;
            if (groupBy === ConsumptionChartBy.Day) {
                return `sum_over_time(${sel}[1h] offset -1h)`;
            }
            return `sum_over_time(${sel}[1d] offset ${timezoneOffset}h)`;
        });

        const combinedQuery = `(${innerExprs.join(" or ")})${subqueryRange}`;

        const response = await fetchMetricData({
            query: combinedQuery,
            time: wsQueryTime,
        });

        return pslMetricDataFromQueryResults(
            get(response, "data.data.result", []),
            { onOrAfter: start, onOrBefore: end },
            groupBy
        );
    };

    const batches =
        batchSize && batchSize > 0 && devices.length > batchSize
            ? chunk(devices, batchSize)
            : [devices];

    const batchResults = await Promise.all(batches.map(fetchBatch));
    const metricData = batchResults.flat();
    const totalOnOff = calculateTotal(metricData) ?? 0;

    return { metricData, totalOnOff };
};

export const getPSLDurationByUnit = (
    duration: number,
    unit: moment.unitOfTime.Base,
    sampleIntervalSeconds: number = PSL_USAGE_AVG_SAMPLE_SECONDS
) =>
    moment
        .duration(duration * sampleIntervalSeconds, "seconds")
        .as(unit);

export const getPSLUsageMetric = async (
    {
        devices,
        groupBy,
        queryTime,
        colors
    }: {
        devices: SensorDetails[];
        groupBy: ConsumptionChartBy;
        queryTime: number;
        colors: string[]
    }
) => {
    const endTime = moment.unix(queryTime);

    const durationUnitMap: Record<
        ConsumptionChartBy,
        moment.unitOfTime.Base
    > = {
        [ConsumptionChartBy.Day]: "hours",
        [ConsumptionChartBy.Week]: "hours",
        [ConsumptionChartBy.Month]: "hours",
        [ConsumptionChartBy.Year]: "hours",
    };
    const durationUnit = durationUnitMap[groupBy];

    const { metricData } = await getPSLUsageMetricData({ devices, groupBy, queryTime });

    const { series, categories } = generateChartSeries({
        sensorDetails: devices,
        metricData,
        lastTime: endTime,
        groupBy,
    });

    const onTimeData: (number | undefined)[][] = [];
    for (const element of series) {
        const s = element;
        if (!s.id?.endsWith("_0")) {
            continue;
        }
        onTimeData.push(
            s.data.map((d) =>
                d === undefined || d === null
                    ? undefined
                    : getPSLDurationByUnit(Number(d), durationUnit)
            )
        );
    }

    return {
        categories,
        series: [{
            id: `room_on_time`,
            data: sumArrays(onTimeData),
            type: "bar",
            barWidth: "60%",
            name: `Room ON Time`,
            color: colors[0],
            stack: ""
        }],
        total: [calculatePSLUsageTotalSeconds(metricData)],
        durationUnit: durationUnit
    };
}

export const fetchGatewayOnlineStatus = async (gatewayIds: string[]) => {
    const heartbeatData = await fetchGatewayHeartbeat({ gatewayIds });

    const gatewayStatus = gatewayIds.reduce((acc, gatewayId) => {
        const data = heartbeatData.get(gatewayId);
        const heartbeats = data ? data.values : [];
        const lastHeartbeat =
            heartbeats.length > 0 ? heartbeats[heartbeats.length - 1][0] : null;
        const isOnline =
            !!lastHeartbeat &&
            getDeviceStatus(lastHeartbeat, true) === DeviceStatus.Online;
        acc[gatewayId] = {
            isOnline,
            heartbeat: lastHeartbeat,
        };

        return acc;
    }, {} as Record<string, { isOnline: boolean; heartbeat: number | null }>);

    return gatewayStatus;
};

export const fetchGatewayHeartbeat = async ({
    gatewayIds,
    startTime,
    endTime,
}: {
    gatewayIds: string[];
    startTime?: number;
    endTime?: number;
}) => {
    const validGatewayIds = gatewayIds.filter((gatewayId) => !!gatewayId?.trim());
    if (!validGatewayIds.length) {
        return new Map();
    }

    // Default to 1 minute range (last data point)
    const defaultDuration = 60; // seconds
    const toTimestamp = endTime ?? moment().unix();
    const fromTimestamp = startTime ?? toTimestamp - defaultDuration;
    const range = convertSecondsToPrometheusTime(toTimestamp - fromTimestamp);

    const metric = "mqtt2tsdb_heartbeat_na";
    const label = "gateway";
    const selectors = validGatewayIds
        .map((gatewayId) => `${metric}{${label}="${gatewayId}"}`)
        .join(" OR ");
    const query = `(${selectors})[${range}:1m]`;

    const queryPayload: any = { query };
    if (endTime) {
        queryPayload["time"] = endTime;
    }

    const response = await fetchMetricData(queryPayload);
    const result = get(response, "data.data.result", []) as any[];

    const resultMap = new Map(
        result.map((item) => {
            const gatewayId = item.metric[label];
            return [gatewayId, item];
        })
    );

    return resultMap;
};

export const fetchGatewayHeartbeatData = async ({
    gatewayIds,
    startTime,
    endTime,
}: {
    gatewayIds: string[];
    startTime?: number;
    endTime?: number;
}) => {
    const heartbeatData = await fetchGatewayHeartbeat({
        gatewayIds,
        startTime,
        endTime,
    });

    const formattedResults = gatewayIds.reduce(
        (acc, gatewayId) => {
            const data = heartbeatData.get(gatewayId);
            const timestamps = data
                ? data.values.map((entry: any) => entry[0])
                : [];
            let totalMinutes = 0;

            // Group timestamps by day and count unique minutes with data
            const groupByDay = timestamps.reduce(
                (acc: Record<number, Set<number>>, timestamp: number) => {
                    // Get start of day timestamp (midnight) in seconds
                    const dayStart = moment
                        .unix(timestamp)
                        .startOf("day")
                        .unix();

                    // Get start of minute timestamp (remove seconds) to count unique minutes
                    const minuteStart = moment
                        .unix(timestamp)
                        .startOf("minute")
                        .unix();

                    // Initialize Set for this day if not exists
                    if (!acc[dayStart]) {
                        acc[dayStart] = new Set();
                    }

                    // Add minute timestamp to Set (automatically handles duplicates within same minute)
                    acc[dayStart].add(minuteStart);

                    return acc;
                },
                {}
            );

            // Convert Sets to counts
            const groupByDayCount: Record<number, number> = {};
            Object.keys(groupByDay).forEach((dayStart) => {
                groupByDayCount[Number(dayStart)] =
                    groupByDay[Number(dayStart)].size;
                totalMinutes += groupByDayCount[Number(dayStart)];
            });

            acc[gatewayId] = {
                groupByDay: groupByDayCount,
                totalMinutes,
                lastHeartbeat:
                    data && data.values.length > 0
                        ? data.values[data.values.length - 1][0]
                        : null,
            };

            return acc;
        },
        {} as Record<
            string,
            {
                groupByDay: Record<number, number>;
                totalMinutes: number;
                lastHeartbeat: number | null;
            }
        >
    );

    return formattedResults;
};

export const fetchHeartbeatSumData = async ({
    gatewayIds,
    startTime,
    endTime,
    groupBy = "1d",
}: {
    gatewayIds: string[];
    startTime: number;
    endTime?: number;
    groupBy?: string;
}) => {
    const fromTimestamp = startTime;
    const toTimestamp = endTime ?? moment().unix();
    const range = convertSecondsToPrometheusTime(toTimestamp - fromTimestamp);

    const currentTzOffset = moment().utcOffset() / 60;
    const metric = "mqtt2tsdb_heartbeat_na";
    const label = "gateway";
    const selectors = gatewayIds.filter((id) => !!String(id).trim()).join("|");

    // Ex: count_over_time(mqtt2tsdb_heartbeat_na{gateway=~\"PH60XXRR05282196705|PH80XXRR25577161989|PH61XXRR05282196705\"}[1d] offset 7h)[90d:1d]
    const query = `count_over_time(${metric}{${label}=~"${selectors}"}[${groupBy}] offset ${currentTzOffset}h)[${range}:${groupBy}]`;

    const queryPayload: any = { query };
    if (endTime) {
        queryPayload["time"] = endTime;
    }

    const response = await fetchMetricData(queryPayload);
    const result = get(response, "data.data.result", []) as any[];

    const results: {
        [gatewayId: string]: {
            groupByDay: Record<number, number>;
            totalMinutes: number;
        };
    } = {};

    result.forEach((item) => {
        const gatewayId = item.metric[label];
        const groupByDay: Record<number, number> = {};
        let totalMinutes = 0;

        (item.values ?? []).forEach((entry: any) => {
            // BE always adds timezone offset based on the offset in the query
            // Need to subtract it back to get the correct start of day timestamp
            const startOfDay = entry[0] - currentTzOffset * 3600;
            const heartbeatCount = Number(entry[1]);

            groupByDay[startOfDay] = heartbeatCount;
            totalMinutes += heartbeatCount;
        });

        results[gatewayId] = {
            groupByDay,
            totalMinutes,
        };
    });

    return results;
};

